import React, { useEffect, useState, useMemo } from 'react';
import { Box, FormControl } from '@mui/material';
import Select, { MultiValue, SingleValue } from 'react-select';

type organizationUserOptionEntry = {
  value: string;
  label: string;
  data?: { [key: string]: any };
};

interface MultiValueInputProps {
  label: string;
  data: any[];
  mapperFn?: (input: any) => {
    value: string;
    label: string;
    data?: { [key: string]: any };
  };
  minDataCount?: number;
  filterOptions?: (candidate: { label: string; value: string; data: any }, input: string) => boolean;
  value: any[] | any;
  onValueChange: (inputValue: any) => void;
  isDisabled?: boolean;
  handleInputChange?: (inputValue: string) => void;
  isMulti?: boolean;
  placeholder?: string;
}

export const MultiValueInput = (props: MultiValueInputProps) => {
  const [selectedOptions, setSelectedOptions] = useState<MultiValue<organizationUserOptionEntry>>([]);

  const isAccountIdMode = useMemo(
    () =>
      Array.isArray(props.data) &&
      props.data.length > 0 &&
      typeof props.data[0] === 'object' &&
      props.data[0] !== null &&
      'accountId' in props.data[0],
    [props.data]
  );

  const options: organizationUserOptionEntry[] = useMemo(() => {
    const opts: organizationUserOptionEntry[] = [];
    if (Array.isArray(props.value) && !props.mapperFn && props.data.length > 0) {
      if (typeof props.data[0] === 'object' && props.data[0] !== null && 'accountId' in props.data[0]) {
        props.data.forEach((entry: any) => {
          if (entry.accountId && entry.publicName) {
            opts.push({ value: entry.accountId, label: entry.publicName, data: entry });
          }
        });
      } else if (typeof props.data[0] === 'string') {
        // Fallback for string array
        (props.data as string[]).forEach(entry => {
          if (entry) {
            opts.push({ value: entry, label: entry });
          }
        });
      }
    } else if (Array.isArray(props.value) && props.mapperFn) {
      props.data.map(props.mapperFn).forEach(entry => {
        opts.push({
          value: entry.value,
          label: entry.label,
          data: entry.data,
        });
      });
    }
    return opts;
  }, [props.data, props.value, props.mapperFn]);

  useEffect(() => {
    const initialTeamMembersMultiValue: organizationUserOptionEntry[] = [];

    if (Array.isArray(props.value)) {
      props.value.forEach(entry => {
        if (isAccountIdMode) {
          const userObj = props.data.find((user: any) => user.accountId === entry);
          if (userObj) {
            initialTeamMembersMultiValue.push({ value: entry, label: userObj.publicName });
          }
        } else {
          initialTeamMembersMultiValue.push({ value: entry, label: entry });
        }
      });
    }

    setSelectedOptions(initialTeamMembersMultiValue);
  }, [props.value, props.data, isAccountIdMode]);

  const optionsToSelect = useMemo(
    () =>
      options.filter(opt => !(selectedOptions as organizationUserOptionEntry[]).some(item => item.value === opt.value)),
    [options, selectedOptions]
  );
  const handleInputChange = (newValue: string): void | string => {
    if (props.isMulti === undefined || props.isMulti) {
      const inputArr = newValue.split(';');
      const selectableValues: organizationUserOptionEntry[] = [];
      const inputToChange = inputArr.filter(input => {
        const matchedValue = optionsToSelect.find(
          opt =>
            opt.data?.email?.toLowerCase() === input.trim().toLowerCase() ||
            opt.data?.emailAddress?.toLowerCase() === input.trim().toLowerCase() ||
            opt.label.toLowerCase() === input.trim().toLowerCase()
        );
        if (matchedValue) {
          selectableValues.push(matchedValue);
          return false;
        }
        return true;
      });
      if (selectableValues.length > 0) {
        const selectedValues: organizationUserOptionEntry[] = [...selectedOptions, ...selectableValues];
        setSelectedOptions(prevOptions => [...prevOptions, ...selectableValues]);
        props.onValueChange(selectedValues.map(option => option.value));
      }

      return inputToChange.join(';');
    }
  };

  const handleSelect = (
    eventData: MultiValue<organizationUserOptionEntry> | SingleValue<organizationUserOptionEntry>
  ) => {
    const formattedData: string[] = [];
    if (Array.isArray(eventData)) {
      eventData.forEach(element => {
        formattedData.push(element.value);
      });
      setSelectedOptions(eventData);
    } else {
      formattedData.push((eventData as any).value);
      setSelectedOptions([eventData as MultiValueInputProps]);
    }
    props.onValueChange(formattedData);
  };

  return (
    <FormControl sx={{ flex: 1 }}>
      <Box sx={{ m: 2 }}>
        <div className="dropdown-container">
          <h2>{props.label}</h2>
          <Select
            options={options}
            isDisabled={props.isDisabled}
            placeholder={props.placeholder}
            filterOption={props.filterOptions}
            value={selectedOptions}
            onChange={handleSelect}
            onInputChange={props.handleInputChange || handleInputChange}
            isSearchable
            isMulti={props.isMulti !== undefined ? props.isMulti : true}
          />
        </div>
      </Box>
    </FormControl>
  );
};
